import { useEffect, useMemo, useRef, useState } from 'react';
import { Dataset } from '../types/types';
import { getDatasetWithInitialVisibility } from '../utils/dataset/getDatasetWithInitialVisibility';
import { hasDynamicItemVisibility } from '../utils/dataset/hasDynamicItemVisibility';
import { hasSameItemVisibility } from '../utils/dataset/hasSameItemVisibility';
import { resolveDatasetItemsVisibility } from '../utils/dataset/resolveDatasetItemsVisibility';
import { debugLogger } from '../debug/DebugLogger';

const log = debugLogger.scope('visibility');

const RESOLVE_DEBOUNCE_MS = 250;

// Resolves dynamic item visibility JSONata expressions on dataset load and on
// every filter change (debounced, first run immediate — same as
// useEditableResolve). Expression items stay hidden until resolved; when a
// re-evaluation changes nothing the previous dataset identity is kept to avoid
// churn in consumers.
export const useDatasetVisibilityResolve = (
  dataset: Dataset | undefined,
  activeFilters: Record<string, unknown> | null
): Dataset | undefined => {
  // `source` ties the resolved value to the raw dataset
  const [resolvedDatasetItems, setResolvedDatasetItems] = useState<{
    source: Dataset;
    value: Dataset;
  } | null>(null);
  // Ref instead of state: only skips the debounce, must not trigger a re-render
  const isFirstRun = useRef(true);

  const visibilityResolvedDataset = useMemo(() => {
    if (!dataset) {
      return undefined;
    }
    if (resolvedDatasetItems?.source === dataset) {
      return resolvedDatasetItems.value;
    }
    return getDatasetWithInitialVisibility(dataset);
  }, [dataset, resolvedDatasetItems]);

  useEffect(() => {
    if (!dataset || !hasDynamicItemVisibility(dataset)) {
      setResolvedDatasetItems(null);
      return;
    }

    let cancelled = false;
    const delay = isFirstRun.current ? 0 : RESOLVE_DEBOUNCE_MS;
    isFirstRun.current = false;

    // Debounce: each dep change reschedules; resolve runs only after deps stop
    // changing for RESOLVE_DEBOUNCE_MS.
    const timeoutId = setTimeout(() => {
      resolveDatasetItemsVisibility(dataset, activeFilters)
        .then((resolved) => {
          if (cancelled) {
            return;
          }
          setResolvedDatasetItems((previous) => {
            if (
              previous?.source === dataset &&
              hasSameItemVisibility(previous.value, resolved)
            ) {
              return previous;
            }

            /** @debugFeature visibility Logs a changed dataset item visibility: which columns ended up hidden in the grid and in the detail. */
            log('dataset items visibility resolved', () => ({
              datasetId: dataset.datasetId,
              hidden: (resolved.items ?? [])
                .filter((item) => item.visible === false)
                .map((item) => item.name),
              detailHidden: (resolved.items ?? [])
                .filter((item) => item.detailVisible === false)
                .map((item) => item.name),
            }));

            return { source: dataset, value: resolved };
          });
        })
        .catch((error) => {
          console.error(
            '[useDatasetVisibilityResolve] Failed to resolve dataset items visibility.',
            error
          );

          /** @debugFeature visibility Logs a failed dataset items visibility resolve — expression items remain hidden. */
          log('dataset visibility resolve failed, items remain hidden', () => ({
            datasetId: dataset.datasetId,
            error,
          }));
        });
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [dataset, activeFilters]);

  return visibilityResolvedDataset;
};
